import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Database, ArrowLeft, Download, Settings, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '../api/client';

type CoverageData = {
  tickets_total: number;
  tickets_hde: number;
  users_hde: number;
  orgs_hde: number;
  by_priority: Record<string, number>;
  by_status: Record<string, number>;
  tickets_with_notes: number;
  notes_top_keys: { key: string; count: number }[];
  markdown: string;
};

const STATUS_LABEL: Record<string, string> = {
  new: 'Новые', open: 'В работе', pending_customer: 'Ждём клиента',
  on_hold: 'На паузе', resolved: 'Решены', closed: 'Закрыты', cancelled: 'Отменены',
};

export default function HDECoveragePage() {
  const [data, setData] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api.get('/api/v1/hde/coverage')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const download = () => {
    if (!data?.markdown) return;
    const blob = new Blob([data.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hde-coverage-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runBackfill = async () => {
    if (!window.confirm('Запустить разбор notes? Распознанные поля (домены / сайт / B24-ссылка) переедут в типизированные колонки организаций. Действие идемпотентное — повторный запуск ничего не сломает.')) return;
    setBackfillBusy(true); setBackfillResult(null);
    try {
      const r = await api.post('/api/v1/hde/backfill');
      const f = r.data.fields_set || {};
      const parts = [`Просканировано: ${r.data.orgs_scanned}`, `Обновлено: ${r.data.orgs_updated}`];
      if (Object.keys(f).length > 0) {
        parts.push('Перенесено:');
        for (const [k, v] of Object.entries(f)) parts.push(`  ${k}: ${v}`);
      } else {
        parts.push('Новых распознаваний не найдено.');
      }
      setBackfillResult(parts.join('\n'));
      load();
    } catch (e: any) {
      setBackfillResult('Ошибка: ' + (e?.response?.data?.detail || e?.message));
    } finally { setBackfillBusy(false); }
  };

  if (loading) return (
    <div className="text-slate-500 text-sm py-8 text-center">
      <Loader2 className="inline animate-spin mr-2" size={14} />считаем…
    </div>
  );
  if (!data) return <div className="text-slate-500">Не удалось загрузить</div>;

  const pctImport = data.tickets_total > 0
    ? ((data.tickets_hde / data.tickets_total) * 100).toFixed(1) : '0';

  return (
    <section className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to="/settings" className="text-xs text-slate-500 hover:text-blue-400 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Назад в Настройки
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2 mt-1">
            <Database className="text-purple-400" size={22} /> Отчёт по импорту из HelpDeskEddy
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {data.markdown && (
            <button onClick={download}
                    title="Скачать markdown-отчёт"
                    className="text-xs inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded font-bold">
              <Download size={12} /> .md
            </button>
          )}
        </div>
      </div>

      {/* Объяснение: что это вообще */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 text-sm text-slate-300 space-y-2">
        <p>
          В Service Desk перевезена история обращений из старой системы поддержки
          <b className="text-white"> HelpDeskEddy</b> (`iqr.helpdeskeddy.com`).
          Эта страница показывает, насколько ровно сработал импорт: сколько тикетов
          и клиентов перенесено, как распределены их статусы и приоритеты, и какие
          «нестандартные» поля HDE не смог разобрать автоматически.
        </p>
        <p className="text-xs text-slate-500">
          Каждый импортированный объект помечен полем <code>hde_id</code> — это
          идентификатор записи в исходной HDE. Повторный запуск импорта обновляет
          существующие, а не дублирует.
        </p>
      </div>

      {/* Сводные карточки */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SumCard label="Тикетов перенесено" value={data.tickets_hde}
                 hint={`${pctImport}% от всех тикетов в SD (всего ${data.tickets_total})`}
                 color="purple" />
        <SumCard label="Пользователей" value={data.users_hde}
                 hint="клиенты, перенесённые из HDE" color="blue" />
        <SumCard label="Организаций" value={data.orgs_hde}
                 hint="всего создано / обновлено" color="green" />
        <SumCard label="С нераспознанными полями" value={data.tickets_with_notes}
                 hint="организаций с «сырыми» HDE custom_fields в notes"
                 color="orange" />
      </div>

      {/* Распределение приоритетов и статусов */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <DistroCard title="Приоритеты импортированных тикетов"
                    data={data.by_priority}
                    explainer="HDE не всегда передавал приоритет. Перекос в P4 = у большинства тикетов priority пришёл пустым и встал в дефолт."
                    color="amber" />
        <DistroCard title="Статусы на момент импорта"
                    data={data.by_status}
                    labels={STATUS_LABEL}
                    explainer="Сколько импортированных тикетов в каком состоянии сейчас."
                    color="blue" />
      </div>

      {/* Нераспознанные поля */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
          <AlertTriangle size={14} className="text-orange-400" />
          <h2 className="font-bold text-white text-sm">Нераспознанные поля HDE</h2>
        </header>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-300">
            Когда HDE отдавал custom_fields, которые не сопоставлены с типизированными
            полями SD (`legal_address`, `contact_phone`, `domains`, `website`, ссылка на B24…),
            мы кладём их в свободное поле <code>notes</code> как строки вида
            <code className="bg-slate-800 px-1 rounded">{'«Ключ: значение»'}</code>.
            Ниже — самые частые ключи. Если какой-то из них стоит замапить в отдельное поле,
            нужно расширить маппинг в <code>services/hde_import.py</code>.
          </p>
          {data.notes_top_keys.length === 0 ? (
            <div className="text-emerald-400 text-sm inline-flex items-center gap-1.5">
              <CheckCircle2 size={14} /> Все ключи уже разобраны типизированно — ничего в notes не висит.
            </div>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.notes_top_keys.map(it => (
                <li key={it.key}
                    className="flex items-center justify-between bg-slate-950/50 border border-slate-800 rounded px-3 py-1.5">
                  <code className="text-orange-300">{it.key}</code>
                  <span className="text-slate-400 text-xs">{it.count} раз</span>
                </li>
              ))}
            </ul>
          )}

          <div className="bg-slate-950/40 border border-slate-800 rounded p-3 mt-3">
            <div className="text-xs text-slate-400 mb-2">
              <b className="text-white">Backfill</b> — попробовать разобрать уже импортированные
              notes автоматически: перенесёт известные ключи (Домены / Сайт / B24)
              в типизированные поля организаций. Идемпотентно.
            </div>
            <button onClick={runBackfill} disabled={backfillBusy}
                    className="text-xs inline-flex items-center gap-1.5 bg-purple-700 hover:bg-purple-600 disabled:bg-slate-700 text-white px-3 py-1.5 rounded font-bold">
              {backfillBusy ? <Loader2 size={12} className="animate-spin" /> : <Settings size={12} />}
              Запустить backfill notes → поля
            </button>
            {backfillResult && (
              <pre className="mt-2 text-xs text-slate-300 whitespace-pre-wrap bg-slate-950 border border-slate-800 rounded p-2 font-mono">{backfillResult}</pre>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function SumCard({ label, value, hint, color }:
  { label: string; value: number; hint?: string; color: 'purple' | 'blue' | 'green' | 'orange' }) {
  const c = {
    purple: 'border-purple-900/60 bg-purple-950/30',
    blue: 'border-blue-900/60 bg-blue-950/30',
    green: 'border-green-900/60 bg-green-950/30',
    orange: 'border-orange-900/60 bg-orange-950/30',
  }[color];
  return (
    <div className={`rounded-lg border ${c} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value.toLocaleString('ru-RU')}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function DistroCard({ title, data, labels, explainer, color }:
  { title: string; data: Record<string, number>; labels?: Record<string, string>; explainer?: string; color: 'amber' | 'blue' }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  const max = entries.length ? Math.max(...entries.map(([, v]) => v)) : 1;
  const barColor = color === 'amber' ? 'bg-amber-500/60' : 'bg-blue-500/60';
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <h3 className="text-sm font-bold text-white mb-2">{title}</h3>
      {explainer && <p className="text-[11px] text-slate-500 mb-3">{explainer}</p>}
      {entries.length === 0 ? (
        <div className="text-slate-500 text-xs">Нет данных</div>
      ) : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="text-xs">
              <div className="flex items-baseline justify-between">
                <span className="text-slate-300">{labels?.[k] || k}</span>
                <span className="text-white font-bold font-mono">{v}</span>
              </div>
              <div className="h-1 mt-0.5 bg-slate-800 rounded overflow-hidden">
                <div className={`h-full ${barColor}`} style={{ width: `${(v / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
